import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import {
  Pill, Plus, X, Bell, BellOff, Check, SkipForward,
  Calendar, Clock, Trash2, Edit2, ChevronLeft, ChevronRight,
  History, FileDown, Share2
} from 'lucide-react';
import { toast } from 'sonner';

import { getFCMToken, onForegroundMessage } from '../../services/firebase';
import api from '../../services/api';

const API_URL = '';
const DAYS_OF_WEEK = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];

export default function MedicationReminder({ onBack, onNavigate, isExpired }) {
  const [medications, setMedications] = useState([]);
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingMed, setEditingMed] = useState(null);
  const [notificationsEnabled, setNotificationsEnabled] = useState(false);
  const [selectedDate, setSelectedDate] = useState(new Date().toISOString().split('T')[0]);
  const [view, setView] = useState('today'); // 'today' | 'history'
  const [fcmDiag, setFcmDiag] = useState(null); // diagnostic info

  const [form, setForm] = useState({
    name: '', dosage: '', frequency: 'daily',
    times: ['08:00'], days_of_week: [],
    start_date: new Date().toISOString().split('T')[0],
    end_date: '', notes: ''
  });

  const getToken = useCallback(() => localStorage.getItem('gym_notebook_auth_token'), []);

  const headers = useCallback(() => ({
    'Content-Type': 'application/json',
    'Authorization': `Bearer ${getToken()}`
  }), [getToken]);

  // Load medications
  const loadMedications = useCallback(async () => {
    try {
      const res = await fetch(`${API_URL}/api/v2/medications`, { headers: headers() });
      if (res.ok) {
        const data = await res.json();
        setMedications(data.medications || []);
      }
    } catch (e) {
      console.error('Error loading medications:', e);
    }
  }, [headers]);

  // Load logs for selected date
  const loadLogs = useCallback(async () => {
    try {
      const res = await fetch(`${API_URL}/api/v2/medications/logs?date=${selectedDate}`, { headers: headers() });
      if (res.ok) {
        const data = await res.json();
        setLogs(data.logs || []);
      }
    } catch (e) {
      console.error('Error loading logs:', e);
    }
  }, [headers, selectedDate]);

  useEffect(() => {
    const init = async () => {
      setLoading(true);
      await Promise.all([loadMedications(), loadLogs()]);
      setLoading(false);
    };
    init();
  }, [loadMedications, loadLogs]);

  // Check notification permission and sync with cloud preference
  useEffect(() => {
    const browserGranted = 'Notification' in window && Notification.permission === 'granted';
    
    // Check cloud preference first for cross-device consistency
    (async () => {
      try {
        const cloud = await api.get('/sync/cloud');
        const cloudPref = cloud?.data?.medications_notifications_desired === 'true';
        
        if (browserGranted) {
          // Browser granted — register FCM and show ON
          setNotificationsEnabled(true);
          const diag = { steps: [], timestamp: new Date().toLocaleTimeString() };
          diag.steps.push('Auto-register on page load');
          diag.steps.push(`Permission: ${Notification.permission}`);
          try {
            const fcmToken = await getFCMToken();
            if (fcmToken) {
              diag.steps.push(`OK: Token obtained (${fcmToken.length} chars)`);
              const res = await fetch(`${API_URL}/api/v2/medications/fcm-token`, {
                method: 'POST', headers: headers(),
                body: JSON.stringify({ token: fcmToken })
              });
              if (res.ok) {
                const data = await res.json();
                diag.steps.push(`OK: Token saved (${data.token_count} devices)`);
              } else {
                diag.steps.push(`FAIL: Backend returned ${res.status}`);
              }
            }
          } catch (e) {
            diag.steps.push(`ERROR: ${e.message}`);
          }
          setFcmDiag(diag);
        } else if (cloudPref) {
          // Cloud says user wants notifications but this device hasn't granted yet
          // Show as ON (consistent with other devices)
          setNotificationsEnabled(true);
        } else {
          setNotificationsEnabled(false);
        }
      } catch {
        // Fallback to browser permission only
        setNotificationsEnabled(browserGranted);
      }
    })();
  }, []);

  // Listen for foreground messages
  useEffect(() => {
    let unsub;
    try {
      unsub = onForegroundMessage((payload) => {
        toast.info(payload.notification?.title || 'Medication Reminder', {
          description: payload.notification?.body
        });
        loadLogs();
      });
    } catch (e) { /* messaging not available */ }
    return () => { if (unsub) unsub(); };
  }, [loadLogs]);

  // Listen for notification actions from service worker
  useEffect(() => {
    const handler = (event) => {
      if (event.data?.type === 'MEDICATION_ACTION') {
        const { action, medicationId, scheduledTime } = event.data;
        if (action === 'taken' || action === 'skip') {
          logMedication(medicationId, scheduledTime, action === 'taken' ? 'taken' : 'skipped');
        }
        loadLogs();
      }
    };
    navigator.serviceWorker?.addEventListener('message', handler);
    return () => navigator.serviceWorker?.removeEventListener('message', handler);
  }, []);

  // Disable notifications - remove FCM token from backend
  const disableNotifications = async () => {
    try {
      const fcmToken = await getFCMToken().catch(() => null);
      if (fcmToken) {
        await fetch(`${API_URL}/api/v2/medications/fcm-token`, {
          method: 'DELETE', headers: headers(),
          body: JSON.stringify({ token: fcmToken })
        });
      }
      setNotificationsEnabled(false);
      toast.success('Reminders disabled. Tap "Reminders" to re-enable.');
      // Persist preference to cloud
      api.post('/sync/cloud', { data: { medications_notifications_desired: 'false' } }).catch(() => {});
    } catch (e) {
      // Even if delete fails, still toggle off locally
      setNotificationsEnabled(false);
      toast.info('Reminders turned off locally.');
    }
  };


  // Enable notifications
  const enableNotifications = async () => {
    const diag = { steps: [], timestamp: new Date().toLocaleTimeString() };
    
    if (!('Notification' in window)) {
      diag.steps.push('FAIL: Notifications API not available');
      setFcmDiag(diag);
      toast.error('Notifications not supported on this device.');
      return;
    }
    diag.steps.push('OK: Notifications API available');
    
    const isPWA = window.matchMedia('(display-mode: standalone)').matches || window.navigator.standalone;
    diag.steps.push(`Mode: ${isPWA ? 'PWA' : 'Browser'}`);
    
    if (Notification.permission === 'denied') {
      diag.steps.push('FAIL: Permission denied');
      setFcmDiag(diag);
      if (isPWA) {
        toast.error('Notifications blocked. Go to device Settings → Apps → NutriSteps → Notifications.', { duration: 6000 });
      } else {
        toast.error('Notifications blocked. Click the lock icon in the address bar → Allow notifications.', { duration: 6000 });
      }
      return;
    }
    diag.steps.push(`Permission: ${Notification.permission}`);
    
    try {
      toast.loading('Setting up notifications...', { id: 'fcm-setup' });
      
      // Step 1: Check service worker support
      const hasSW = 'serviceWorker' in navigator;
      diag.steps.push(`ServiceWorker: ${hasSW ? 'supported' : 'NOT supported'}`);
      if (!hasSW) {
        diag.steps.push('FAIL: No service worker support');
        setFcmDiag(diag);
        toast.error('Push notifications require a newer browser.', { id: 'fcm-setup' });
        return;
      }
      
      // Step 2: Get FCM token
      diag.steps.push('Getting FCM token...');
      const fcmToken = await getFCMToken();
      
      if (!fcmToken) {
        diag.steps.push('FAIL: getToken returned null/empty');
        setFcmDiag(diag);
        toast.error('Could not get notification token. Close the app completely, reopen and try again.', { id: 'fcm-setup', duration: 5000 });
        return;
      }
      diag.steps.push(`OK: Token obtained (${fcmToken.length} chars)`);
      
      // Step 3: Register token on backend
      const res = await fetch(`${API_URL}/api/v2/medications/fcm-token`, {
        method: 'POST', headers: headers(),
        body: JSON.stringify({ token: fcmToken })
      });
      
      if (!res.ok) {
        const errData = await res.json().catch(() => ({}));
        diag.steps.push(`FAIL: Backend ${res.status} - ${errData.detail || 'unknown'}`);
        setFcmDiag(diag);
        throw new Error(errData.detail || `Server error ${res.status}`);
      }
      
      const regData = await res.json();
      diag.steps.push(`OK: Token saved (${regData.token_count} device${regData.token_count > 1 ? 's' : ''})`);
      
      setNotificationsEnabled(true);
      setFcmDiag(diag);
      toast.success(`Reminders enabled! Token saved.`, { id: 'fcm-setup' });
      // Persist preference to cloud so other devices can auto-enable
      api.post('/sync/cloud', { data: { medications_notifications_desired: 'true' } }).catch(() => {});
    } catch (e) {
      const errMsg = e.message || String(e);
      diag.steps.push(`ERROR: ${errMsg}`);
      setFcmDiag(diag);
      console.error('[FCM] Enable error:', errMsg);
      
      if (errMsg.includes('permission') || errMsg.includes('denied')) {
        toast.error('Notifications blocked. Check device Settings → Notifications.', { id: 'fcm-setup', duration: 6000 });
      } else {
        toast.error('Setup failed: ' + errMsg, { id: 'fcm-setup', duration: 6000 });
      }
    }
  };

  // Test notification
  const testNotification = async () => {
    try {
      // First re-register FCM token to ensure it's fresh
      try {
        const fcmToken = await getFCMToken();
        if (fcmToken) {
          await fetch(`${API_URL}/api/v2/medications/fcm-token`, {
            method: 'POST', headers: headers(),
            body: JSON.stringify({ token: fcmToken })
          });
        }
      } catch (e) {
        console.warn('Token refresh failed:', e);
      }

      const res = await fetch(`${API_URL}/api/v2/medications/test-notification`, {
        method: 'POST', headers: headers()
      });
      const data = await res.json();
      if (res.ok) {
        if (data.sent > 0) {
          toast.success(`Test notification sent! Check your device in a few seconds.`);
        } else if (data.total_tokens > 0) {
          toast.error(`Send failed for ${data.total_tokens} token(s). The notification service may need a moment. Try again.`);
        } else {
          toast.error('No devices registered. Tap "Reminders" to enable.');
        }
      } else {
        toast.error(data.detail || 'Failed to send test');
      }
    } catch (e) {
      toast.error('Failed to send test notification');
    }
  };

  // Save medication
  const saveMedication = async () => {
    if (!form.name.trim() || !form.dosage.trim()) {
      toast.error('Name and dosage are required');
      return;
    }
    try {
      const url = editingMed
        ? `${API_URL}/api/v2/medications/${editingMed.medication_id}`
        : `${API_URL}/api/v2/medications`;
      const res = await fetch(url, {
        method: editingMed ? 'PUT' : 'POST',
        headers: headers(),
        body: JSON.stringify({ ...form, tz_offset: new Date().getTimezoneOffset() })
      });
      if (res.ok) {
        toast.success(editingMed ? 'Medication updated' : 'Medication added');
        resetForm();
        loadMedications();
      } else {
        const data = await res.json();
        toast.error(data.detail || 'Failed to save');
      }
    } catch (e) {
      toast.error('Error saving medication');
    }
  };

  // Delete medication
  const deleteMedication = async (medId) => {
    if (!window.confirm('Delete this medication and all its logs?')) return;
    try {
      await fetch(`${API_URL}/api/v2/medications/${medId}`, {
        method: 'DELETE', headers: headers()
      });
      toast.success('Medication deleted');
      loadMedications();
      loadLogs();
    } catch (e) {
      toast.error('Error deleting');
    }
  };

  // Log medication (taken/skipped)
  const logMedication = async (medId, scheduledTime, status) => {
    try {
      await fetch(`${API_URL}/api/v2/medications/log`, {
        method: 'POST', headers: headers(),
        body: JSON.stringify({
          medication_id: medId,
          scheduled_time: scheduledTime,
          status
        })
      });
      toast.success(status === 'taken' ? 'Marked as taken' : 'Marked as skipped');
      loadLogs();
    } catch (e) {
      toast.error('Error logging medication');
    }
  };

  const resetForm = () => {
    setShowForm(false);
    setEditingMed(null);
    setForm({
      name: '', dosage: '', frequency: 'daily',
      times: ['08:00'], days_of_week: [],
      start_date: new Date().toISOString().split('T')[0],
      end_date: '', notes: ''
    });
  };

  const openEdit = (med) => {
    setEditingMed(med);
    setForm({
      name: med.name, dosage: med.dosage, frequency: med.frequency,
      times: med.times || ['08:00'], days_of_week: med.days_of_week || [],
      start_date: med.start_date, end_date: med.end_date || '',
      notes: med.notes || ''
    });
    setShowForm(true);
  };

  const addTime = () => setForm({ ...form, times: [...form.times, '12:00'] });
  const removeTime = (i) => setForm({ ...form, times: form.times.filter((_, idx) => idx !== i) });
  const updateTime = (i, val) => {
    const times = [...form.times];
    times[i] = val;
    setForm({ ...form, times });
  };
  const toggleDay = (day) => {
    const days = form.days_of_week.includes(day)
      ? form.days_of_week.filter(d => d !== day)
      : [...form.days_of_week, day];
    setForm({ ...form, days_of_week: days });
  };

  const changeDate = (offset) => {
    const d = new Date(selectedDate);
    d.setDate(d.getDate() + offset);
    setSelectedDate(d.toISOString().split('T')[0]);
  };

  // Build today's schedule
  const todaySchedule = medications
    .filter(m => m.active)
    .filter(m => {
      if (m.start_date && selectedDate < m.start_date) return false;
      if (m.end_date && selectedDate > m.end_date) return false;
      if (m.frequency === 'weekly') {
        const dow = new Date(selectedDate).getDay();
        const adjustedDow = dow === 0 ? 6 : dow - 1;
        if (m.days_of_week?.length && !m.days_of_week.includes(adjustedDow)) return false;
      }
      return true;
    })
    .flatMap(m => (m.times || []).map(time => ({
      ...m, scheduledTime: `${selectedDate}T${time}:00`, time
    })))
    .sort((a, b) => a.time.localeCompare(b.time));

  const getLogStatus = (medId, time) => {
    return logs.find(l =>
      l.medication_id === medId && l.scheduled_time?.startsWith(`${selectedDate}T${time}`)
    );
  };

  const isToday = selectedDate === new Date().toISOString().split('T')[0];

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-emerald-500" />
      </div>
    );
  }

  return (
    <>
    <div className="min-h-screen bg-gradient-to-br from-slate-950 via-slate-900 to-slate-950" data-testid="medication-reminder">
      {/* Header — matches app style with safe area */}
      <header className="sticky top-0 z-50 backdrop-blur-xl bg-slate-950 border-none ios-header shadow-none" style={{ paddingTop: 'max(20px, env(safe-area-inset-top, 20px))', border: 'none' }}>
        <div className="container mx-auto px-2 sm:px-4 py-2 flex items-center justify-between max-w-full overflow-hidden">
          <div className="flex items-center gap-2 sm:gap-3 flex-shrink-0 min-w-0">
            <div className="w-9 h-9 rounded-xl bg-gradient-to-br from-teal-500 to-emerald-600 flex items-center justify-center shadow-lg shadow-teal-500/20 flex-shrink-0">
              <Pill className="w-5 h-5 text-white" />
            </div>
            <h1 className="text-base sm:text-xl font-heading font-bold tracking-tight text-white truncate">Medications</h1>
          </div>
          <div className="flex items-center gap-2 flex-shrink-0">
            {!notificationsEnabled ? (
              <Button onClick={enableNotifications} size="sm" className="bg-amber-600 hover:bg-amber-500 text-xs sm:text-sm h-8 sm:h-9" data-testid="enable-notifications-btn">
                <Bell className="h-3.5 w-3.5 sm:h-4 sm:w-4 mr-1" /> Reminders
              </Button>
            ) : (
              <div className="flex items-center gap-1">
                <Button onClick={disableNotifications} size="sm" variant="ghost" className="text-emerald-400 hover:text-red-400 hover:bg-red-500/20 text-xs h-7 px-2" data-testid="notifications-on-indicator" title="Tap to disable reminders">
                  <Bell className="h-3.5 w-3.5 mr-1" /> ON
                </Button>
              </div>
            )}
            <Button onClick={() => { resetForm(); setShowForm(true); }} size="sm" className="bg-teal-600 hover:bg-teal-500 text-xs sm:text-sm h-8 sm:h-9" data-testid="add-medication-btn">
              <Plus className="h-3.5 w-3.5 sm:h-4 sm:w-4 mr-1" /> Add
            </Button>
          </div>
        </div>
      </header>

      <div className="container mx-auto px-4 pt-4 pb-24">
      <div className="max-w-lg mx-auto space-y-4">

      {/* Share/Export Report */}
      <div className="flex gap-2">
        <Button
          onClick={async () => {
            try {
              toast.loading('Generating report...', { id: 'pdf-export' });
              const res = await fetch(`${API_URL}/api/v2/medications/export-pdf?days=30`, { headers: headers() });
              if (!res.ok) throw new Error('Export failed');
              const blob = await res.blob();
              const url = URL.createObjectURL(blob);
              
              // Try native share (mobile) first
              if (navigator.share && navigator.canShare?.({ files: [new File([blob], 'medication_report.pdf', { type: 'application/pdf' })] })) {
                const file = new File([blob], 'medication_report.pdf', { type: 'application/pdf' });
                await navigator.share({ title: 'Medication History Report', files: [file] });
                toast.success('Report shared!', { id: 'pdf-export' });
              } else {
                // Fallback: download
                const a = document.createElement('a');
                a.href = url;
                a.download = 'medication_report.pdf';
                a.click();
                toast.success('Report downloaded!', { id: 'pdf-export' });
              }
              URL.revokeObjectURL(url);
            } catch (e) {
              if (e.name !== 'AbortError') toast.error('Export failed: ' + e.message, { id: 'pdf-export' });
            }
          }}
          size="sm"
          className="bg-emerald-600 hover:bg-emerald-500 text-white"
          data-testid="export-pdf-btn"
        >
          <Share2 className="h-4 w-4 mr-1" /> Share Report
        </Button>
      </div>

      {/* View Toggle + Date Nav */}
      <div className="flex items-center justify-between">
        <div className="flex gap-1 bg-slate-800 rounded-lg p-1">
          <button onClick={() => setView('today')}
            className={`px-3 py-1.5 rounded-md text-sm font-medium transition-colors ${view === 'today' ? 'bg-emerald-600 text-white' : 'text-slate-400 hover:text-white'}`}
            data-testid="view-today-btn">
            <Clock className="h-4 w-4 inline mr-1" />Today
          </button>
          <button onClick={() => setView('history')}
            className={`px-3 py-1.5 rounded-md text-sm font-medium transition-colors ${view === 'history' ? 'bg-emerald-600 text-white' : 'text-slate-400 hover:text-white'}`}
            data-testid="view-history-btn">
            <History className="h-4 w-4 inline mr-1" />All Meds
          </button>
        </div>
        {view === 'today' && (
          <div className="flex items-center gap-2">
            <Button variant="ghost" size="sm" onClick={() => changeDate(-1)} data-testid="date-prev">
              <ChevronLeft className="h-4 w-4" />
            </Button>
            <span className="text-white text-sm font-medium min-w-[100px] text-center">
              {isToday ? 'Today' : new Date(selectedDate + 'T12:00:00').toLocaleDateString(undefined, { weekday: 'short', month: 'short', day: 'numeric' })}
            </span>
            <Button variant="ghost" size="sm" onClick={() => changeDate(1)} data-testid="date-next">
              <ChevronRight className="h-4 w-4" />
            </Button>
          </div>
        )}
      </div>

      {/* Today's Schedule View */}
      {view === 'today' && (
        <div className="space-y-3">
          {todaySchedule.length === 0 ? (
            <Card className="bg-slate-800/50 border-slate-700">
              <CardContent className="p-8 text-center">
                <Pill className="h-12 w-12 text-slate-600 mx-auto mb-3" />
                <p className="text-slate-400">No medications scheduled for this day.</p>
                <Button onClick={() => { resetForm(); setShowForm(true); }} size="sm" className="mt-3 bg-emerald-600 hover:bg-emerald-500">
                  <Plus className="h-4 w-4 mr-1" /> Add Medication
                </Button>
              </CardContent>
            </Card>
          ) : (
            todaySchedule.map((item, idx) => {
              const log = getLogStatus(item.medication_id, item.time);
              const isTaken = log?.status === 'taken';
              const isSkipped = log?.status === 'skipped';
              return (
                <Card key={`${item.medication_id}-${item.time}-${idx}`}
                  className={`border transition-colors ${
                    isTaken ? 'bg-emerald-500/10 border-emerald-500/30' :
                    isSkipped ? 'bg-slate-800/30 border-slate-600 opacity-60' :
                    'bg-slate-800 border-slate-700'
                  }`}
                  data-testid={`schedule-item-${idx}`}>
                  <CardContent className="p-4">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                          isTaken ? 'bg-emerald-500/20' : isSkipped ? 'bg-slate-600/20' : 'bg-blue-500/20'
                        }`}>
                          {isTaken ? <Check className="h-5 w-5 text-emerald-400" /> :
                           isSkipped ? <SkipForward className="h-5 w-5 text-slate-400" /> :
                           <Pill className="h-5 w-5 text-blue-400" />}
                        </div>
                        <div>
                          <p className={`font-medium ${isTaken ? 'text-emerald-300 line-through' : isSkipped ? 'text-slate-500 line-through' : 'text-white'}`}>
                            {item.name}
                          </p>
                          <p className="text-slate-400 text-sm">{item.dosage}</p>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <span className="text-slate-400 text-sm font-mono">{item.time}</span>
                        {!log && (
                          <div className="flex gap-1">
                            <Button size="sm" onClick={() => logMedication(item.medication_id, item.scheduledTime, 'taken')}
                              className="bg-emerald-600 hover:bg-emerald-500 h-8 px-2"
                              data-testid={`take-btn-${idx}`}>
                              <Check className="h-4 w-4" />
                            </Button>
                            <Button size="sm" variant="ghost" onClick={() => logMedication(item.medication_id, item.scheduledTime, 'skipped')}
                              className="text-slate-400 hover:text-slate-300 h-8 px-2"
                              data-testid={`skip-btn-${idx}`}>
                              <SkipForward className="h-4 w-4" />
                            </Button>
                          </div>
                        )}
                        {log && (
                          <span className={`text-xs px-2 py-0.5 rounded-full ${
                            isTaken ? 'bg-emerald-500/20 text-emerald-400' : 'bg-slate-600/20 text-slate-400'
                          }`}>
                            {isTaken ? 'Taken' : 'Skipped'}
                          </span>
                        )}
                      </div>
                    </div>
                    {item.notes && <p className="text-slate-500 text-xs mt-2 ml-13">{item.notes}</p>}
                  </CardContent>
                </Card>
              );
            })
          )}
        </div>
      )}

      {/* All Medications View */}
      {view === 'history' && (
        <div className="space-y-3">
          {medications.length === 0 ? (
            <Card className="bg-slate-800/50 border-slate-700">
              <CardContent className="p-8 text-center">
                <Pill className="h-12 w-12 text-slate-600 mx-auto mb-3" />
                <p className="text-slate-400">No medications added yet.</p>
              </CardContent>
            </Card>
          ) : (
            medications.map(med => (
              <Card key={med.medication_id} className={`border ${med.active ? 'bg-slate-800 border-slate-700' : 'bg-slate-800/50 border-slate-700 opacity-60'}`}
                data-testid={`med-card-${med.medication_id}`}>
                <CardContent className="p-4">
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-center gap-2">
                        <h3 className="text-white font-medium">{med.name}</h3>
                        <span className={`text-xs px-2 py-0.5 rounded-full ${med.active ? 'bg-emerald-500/20 text-emerald-400' : 'bg-slate-600/20 text-slate-400'}`}>
                          {med.active ? 'Active' : 'Inactive'}
                        </span>
                      </div>
                      <p className="text-slate-400 text-sm mt-1">{med.dosage}</p>
                      <div className="flex flex-wrap gap-2 mt-2">
                        <span className="text-xs bg-slate-700 text-slate-300 px-2 py-0.5 rounded">
                          {med.frequency === 'daily' ? 'Daily' : `Weekly: ${(med.days_of_week || []).map(d => DAYS_OF_WEEK[d]).join(', ')}`}
                        </span>
                        {(med.times || []).map((t, i) => (
                          <span key={i} className="text-xs bg-blue-500/20 text-blue-400 px-2 py-0.5 rounded font-mono">{t}</span>
                        ))}
                        {med.end_date && (
                          <span className="text-xs bg-amber-500/20 text-amber-400 px-2 py-0.5 rounded">
                            Until {new Date(med.end_date + 'T12:00:00').toLocaleDateString()}
                          </span>
                        )}
                      </div>
                      {med.notes && <p className="text-slate-500 text-xs mt-2">{med.notes}</p>}
                    </div>
                    <div className="flex gap-1 ml-2">
                      <Button size="sm" variant="ghost" onClick={() => openEdit(med)}
                        className="text-blue-400 hover:text-blue-300 hover:bg-blue-500/20"
                        data-testid={`edit-med-${med.medication_id}`}>
                        <Edit2 className="h-4 w-4" />
                      </Button>
                      <Button size="sm" variant="ghost" onClick={() => deleteMedication(med.medication_id)}
                        className="text-red-400 hover:text-red-300 hover:bg-red-500/20"
                        data-testid={`delete-med-${med.medication_id}`}>
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))
          )}
        </div>
      )}

      {/* Add/Edit Medication Form Modal */}
      {showForm && (
        <div className="fixed inset-0 bg-black/60 flex items-start justify-center z-[60] p-4 pt-12 pb-20 overflow-y-auto">
          <Card className="bg-slate-800 border-slate-700 w-full max-w-md">
            <CardHeader className="flex flex-row items-center justify-between pb-3">
              <CardTitle className="text-white flex items-center gap-2">
                <Pill className="h-5 w-5 text-emerald-400" />
                {editingMed ? 'Edit Medication' : 'Add Medication'}
              </CardTitle>
              <Button size="sm" variant="ghost" onClick={resetForm} className="text-slate-400">
                <X className="h-5 w-5" />
              </Button>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <Label className="text-slate-300">Medication Name *</Label>
                <Input value={form.name} onChange={e => setForm({...form, name: e.target.value})}
                  placeholder="e.g. Vitamin D" className="bg-slate-700 border-slate-600 text-white mt-1"
                  data-testid="med-name-input" />
              </div>
              <div>
                <Label className="text-slate-300">Dosage *</Label>
                <Input value={form.dosage} onChange={e => setForm({...form, dosage: e.target.value})}
                  placeholder="e.g. 1 tablet, 500mg" className="bg-slate-700 border-slate-600 text-white mt-1"
                  data-testid="med-dosage-input" />
              </div>
              <div>
                <Label className="text-slate-300">Frequency</Label>
                <div className="flex gap-2 mt-1">
                  {['daily', 'weekly'].map(f => (
                    <button key={f} onClick={() => setForm({...form, frequency: f})}
                      className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                        form.frequency === f ? 'bg-emerald-600 text-white' : 'bg-slate-700 text-slate-300 hover:bg-slate-600'
                      }`} data-testid={`freq-${f}`}>
                      {f.charAt(0).toUpperCase() + f.slice(1)}
                    </button>
                  ))}
                </div>
              </div>
              {form.frequency === 'weekly' && (
                <div>
                  <Label className="text-slate-300">Days of Week</Label>
                  <div className="flex gap-1 mt-1">
                    {DAYS_OF_WEEK.map((day, i) => (
                      <button key={i} onClick={() => toggleDay(i)}
                        className={`w-10 h-10 rounded-lg text-xs font-medium transition-colors ${
                          form.days_of_week.includes(i) ? 'bg-emerald-600 text-white' : 'bg-slate-700 text-slate-400 hover:bg-slate-600'
                        }`} data-testid={`day-${i}`}>
                        {day}
                      </button>
                    ))}
                  </div>
                </div>
              )}
              <div>
                <Label className="text-slate-300">Reminder Times</Label>
                <div className="space-y-2 mt-1">
                  {form.times.map((time, i) => (
                    <div key={i} className="flex items-center gap-2">
                      <Input type="time" value={time} onChange={e => updateTime(i, e.target.value)}
                        className="bg-slate-700 border-slate-600 text-white flex-1"
                        data-testid={`time-input-${i}`} />
                      {form.times.length > 1 && (
                        <Button size="sm" variant="ghost" onClick={() => removeTime(i)} className="text-red-400">
                          <X className="h-4 w-4" />
                        </Button>
                      )}
                    </div>
                  ))}
                  <Button size="sm" variant="outline" onClick={addTime} className="border-slate-600 text-slate-300 w-full" data-testid="add-time-btn">
                    <Plus className="h-4 w-4 mr-1" /> Add Time
                  </Button>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-slate-300">Start Date</Label>
                  <Input type="date" value={form.start_date} onChange={e => setForm({...form, start_date: e.target.value})}
                    className="bg-slate-700 border-slate-600 text-white mt-1" data-testid="start-date-input" />
                </div>
                <div>
                  <Label className="text-slate-300">End Date (optional)</Label>
                  <Input type="date" value={form.end_date} onChange={e => setForm({...form, end_date: e.target.value})}
                    className="bg-slate-700 border-slate-600 text-white mt-1" data-testid="end-date-input" />
                </div>
              </div>
              <div>
                <Label className="text-slate-300">Notes (optional)</Label>
                <Input value={form.notes} onChange={e => setForm({...form, notes: e.target.value})}
                  placeholder="e.g. Take with food" className="bg-slate-700 border-slate-600 text-white mt-1"
                  data-testid="med-notes-input" />
              </div>
              <div className="flex gap-3 pt-2">
                <Button variant="outline" onClick={resetForm} className="flex-1 border-slate-600 text-slate-300">Cancel</Button>
                <Button onClick={saveMedication} className="flex-1 bg-emerald-600 hover:bg-emerald-500" data-testid="save-medication-btn">
                  {editingMed ? 'Update' : 'Save'}
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      )}
      </div>
      </div>
    </div>
    </>
  );
}
